import { useContext, useEffect, useRef, useState } from "react";
import { GlobalContext } from "../../context/GlobalContext";
import Utility from "../../utils/utility";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon, SparklesIcon, ArrowTopRightOnSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { db } from "../../firebase";
import { SecondaryButtonSmall } from "../../components/Button";
import { variantURL, fallbackToOriginal } from "../../utils/imageVariant";

const STATUS_CONFIG = {
  pending: { label: "Pending", bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-800 dark:text-yellow-200" },
  approved: { label: "Approved", bg: "bg-green-100 dark:bg-green-900/40", text: "text-green-800 dark:text-green-300" },
  rejected: { label: "Rejected", bg: "bg-red-100 dark:bg-red-900/40", text: "text-red-800 dark:text-red-300" },
};

const CATEGORY_LABELS = {
  "house-party": { label: "House Party", emoji: "\uD83C\uDF89" },
  activity: { label: "Activity", emoji: "\uD83C\uDFDE\uFE0F" },
  "speed-friending": { label: "Speed Friending", emoji: "\uD83E\uDD1D" },
};

export default function EventRequests({ embedded = false }) {
  const { globalState } = useContext(GlobalContext);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [viewingSuggestion, setViewingSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [suggestionError, setSuggestionError] = useState(null);
  // Event photos live on the linked AI suggestion, not the request itself.
  // Fetch them for the list, keyed by suggestionID, and cache so snapshot
  // updates don't refetch.
  const [suggestionImages, setSuggestionImages] = useState({});
  const fetchedSuggestionIds = useRef(new Set());

  const openRequest = async (request) => {
    setViewingRequest(request);
    setViewingSuggestion(null);
    setSuggestionError(null);
    if (!request.suggestionID) return;
    setLoadingSuggestion(true);
    try {
      const snap = await getDoc(doc(db, "eventsSuggestions", request.suggestionID));
      if (!snap.exists()) {
        setSuggestionError("This idea no longer exists.");
      } else {
        setViewingSuggestion({ id: snap.id, ...snap.data() });
      }
    } catch (error) {
      console.error("Error loading suggestion:", error);
      setSuggestionError("Failed to load idea.");
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const closeDetails = () => {
    setViewingRequest(null);
    setViewingSuggestion(null);
    setSuggestionError(null);
  };

  useEffect(() => {
    const q = query(
      collection(db, "eventsRequests"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Pull event photos from the linked suggestions, once per suggestionID.
  useEffect(() => {
    const idsToFetch = [
      ...new Set(requests.map((r) => r.suggestionID).filter(Boolean)),
    ].filter((id) => !fetchedSuggestionIds.current.has(id));
    if (idsToFetch.length === 0) return;
    idsToFetch.forEach((id) => fetchedSuggestionIds.current.add(id));

    let cancelled = false;
    Promise.all(
      idsToFetch.map(async (id) => {
        try {
          const snap = await getDoc(doc(db, "eventsSuggestions", id));
          return [id, snap.exists() ? snap.data().imageUrl || null : null];
        } catch (error) {
          console.error("Error loading suggestion image:", error);
          return [id, null];
        }
      })
    ).then((entries) => {
      if (!cancelled) {
        setSuggestionImages((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [requests]);

  const deleteRequest = async (requestId, { onSuccess } = {}) => {
    if (!window.confirm("Are you sure you want to delete this event request? This action cannot be undone.")) return;
    setUpdatingId(requestId);
    try {
      await deleteDoc(doc(db, "eventsRequests", requestId));
      onSuccess?.();
    } catch (error) {
      console.error("Error deleting request:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const handleCreateEvent = (request, suggestion) => {
    const parts = [];
    const reqDate = Utility.convertDate(request.date);
    if (request.eventName) parts.push(`Event: ${request.eventName}`);
    if (request.location) parts.push(`Venue: ${request.location}`);
    if (reqDate) {
      parts.push(`Date: ${reqDate.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}`);
      parts.push(`Time: ${reqDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`);
    }
    const categoryLabel = CATEGORY_LABELS[request.category]?.label;
    if (categoryLabel) parts.push(`Category: ${categoryLabel}`);
    if (request.description) parts.push(`\n${request.description}`);
    if (request.submittedBy?.userName) parts.push(`\nRequested by: ${request.submittedBy.userName}`);

    if (suggestion && suggestion.eventName) {
      parts.push(`\n— Original AI idea —`);
      if (suggestion.theme) parts.push(`Theme: ${suggestion.theme}`);
      if (suggestion.reasoning) parts.push(`Why: ${suggestion.reasoning}`);
    }

    const draftKey = `gistEventDraft:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({
          suggestionMessage: parts.join("\n"),
          suggestionImageUrl: suggestion?.imageUrl || null,
          // Thread the source ids so the creator tab can mark them converted ONLY
          // once the event is actually saved (see FormSection.handleSubmit). We no
          // longer optimistically flip the request to "approved" here — opening the
          // form isn't proof of creation, so it stays "pending" and retryable until
          // the event truly exists, then flips to "approved" live via onSnapshot.
          sourceRequestId: request.id,
          suggestionId: request.suggestionID || null,
        })
      );
    } catch (error) {
      console.error("Failed to stash event draft:", error);
    }
    window.open(`/events/new?draftKey=${encodeURIComponent(draftKey)}`, "_blank", "noopener");
  };

  const updateStatus = async (requestId, newStatus) => {
    setUpdatingId(requestId);
    try {
      const ref = doc(db, "eventsRequests", requestId);
      await updateDoc(ref, {
        status: newStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error updating request status:", error);
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (dateValue) => {
    const date = Utility.convertDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const filteredRequests = requests.filter((req) => {
    const matchesStatus =
      statusFilter === "all" || req.status === statusFilter;
    const searchContent =
      `${req.submittedBy?.userName || ""} ${req.description || ""} ${req.location || ""}`.toLowerCase();
    const matchesSearch = searchContent.includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const wrapperClass = embedded
    ? "mt-8"
    : "min-h-screen bg-gray-100 dark:bg-gray-800 p-4 sm:p-6 lg:p-8";

  if (loading) {
    return (
      <div className={wrapperClass}>
        {!embedded && (
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">
            Event Requests
          </h1>
        )}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 animate-pulse"
            >
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="ml-4 flex-grow">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                </div>
              </div>
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        {!embedded && (
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-0">
            Event Requests
          </h1>
        )}
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {filteredRequests.length} request
          {filteredRequests.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <input
            type="text"
            placeholder="Search by name, description, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 shadow-sm focus:border-primary focus:ring-primary text-sm sm:text-base p-2.5"
          />
          <div className="flex gap-2 sm:flex-shrink-0 overflow-x-auto -mx-1 px-1 sm:mx-0 sm:px-0 sm:overflow-visible">
            {["all", "pending", "approved", "rejected"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`flex-shrink-0 px-3 sm:px-4 py-2 rounded-md text-sm font-medium border transition-colors duration-200 ${
                  statusFilter === status
                    ? "bg-primary text-white border-primary"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-6">
        {filteredRequests.length === 0 ? (
          <p className="col-span-full text-center text-gray-500 dark:text-gray-400 py-12">
            No event requests found.
          </p>
        ) : (
          filteredRequests.map((req) => {
            const statusConfig = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const category = CATEGORY_LABELS[req.category];
            const isUpdating = updatingId === req.id;
            const eventImage = req.suggestionID ? suggestionImages[req.suggestionID] : null;

            return (
              <div
                key={req.id}
                onClick={() => openRequest(req)}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 flex flex-col transition-all duration-200 max-w-xl cursor-pointer hover:shadow-lg hover:ring-1 hover:ring-primary/20"
              >
                {/* Event photo (from the linked AI suggestion) */}
                {eventImage && (
                  <div className="-mx-6 -mt-6 mb-4 aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-t-lg">
                    <img
                      src={variantURL(eventImage, 800)}
                      onError={fallbackToOriginal(eventImage)}
                      alt={req.eventName || ""}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                {/* Event title + status */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <h3 className="flex-1 text-base font-semibold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2">
                    {req.eventName || "Event Request"}
                  </h3>
                  <span
                    className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}
                  >
                    {statusConfig.label}
                  </span>
                </div>

                {/* Submitter */}
                <div className="flex items-center mb-4">
                  <div className="h-9 w-9 flex-shrink-0">
                    {req.submittedBy?.photoURL ? (
                      <img
                        className="h-9 w-9 rounded-full object-cover"
                        src={variantURL(req.submittedBy.photoURL, 200)}
                        onError={fallbackToOriginal(req.submittedBy.photoURL)}
                        alt=""
                      />
                    ) : (
                      <div className="h-9 w-9 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-base font-semibold">
                        {(req.submittedBy?.userName || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="ml-3 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {req.submittedBy?.userName || "Unknown"}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {req.submittedBy?.userEmail ||
                        req.submittedBy?.userPhone ||
                        ""}
                    </div>
                  </div>
                </div>

                {/* Category badge */}
                {category && (
                  <div className="mb-3">
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-200">
                      {category.emoji} {category.label}
                    </span>
                  </div>
                )}

                {/* Description */}
                <p className="text-sm text-gray-700 dark:text-gray-200 mb-4 line-clamp-3">
                  {req.description}
                </p>

                {/* Date & Location */}
                <div className="text-sm text-gray-500 dark:text-gray-400 space-y-1 mb-4">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-600 dark:text-gray-300">When:</span>
                    {formatDate(req.date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-600 dark:text-gray-300">Where:</span>
                    {req.location || "N/A"}
                  </div>
                  {req.createdAt && (
                    <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                      Submitted {formatDate(req.createdAt)}
                    </div>
                  )}
                </div>

                {/* Linked idea indicator */}
                {req.suggestionID && (
                  <span className="mb-3 inline-flex items-center gap-1.5 self-start rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    <SparklesIcon className="h-3.5 w-3.5" />
                    From AI idea
                  </span>
                )}

                {/* Actions */}
                {(req.status === "approved" || req.status === "rejected") && (
                  <div
                    className="mt-auto pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {req.status === "approved" && (
                      <SecondaryButtonSmall
                        onClick={() => updateStatus(req.id, "pending")}
                        disabled={isUpdating}
                      >
                        Revert to Pending
                      </SecondaryButtonSmall>
                    )}
                    {req.status === "rejected" && (
                      <>
                        <SecondaryButtonSmall
                          onClick={() => updateStatus(req.id, "pending")}
                          disabled={isUpdating}
                        >
                          Revert to Pending
                        </SecondaryButtonSmall>
                        <button
                          onClick={() => deleteRequest(req.id)}
                          disabled={isUpdating}
                          className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white border border-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {viewingRequest && (
        <RequestDetailModal
          request={viewingRequest}
          suggestion={viewingSuggestion}
          loadingSuggestion={loadingSuggestion}
          suggestionError={suggestionError}
          onClose={closeDetails}
          onCreateEvent={() => handleCreateEvent(viewingRequest, viewingSuggestion)}
          onDeleteRequest={
            globalState.isSuperAdmin
              ? () => deleteRequest(viewingRequest.id, { onSuccess: closeDetails })
              : null
          }
          isDeleting={updatingId === viewingRequest.id}
          formatDate={formatDate}
        />
      )}
    </div>
  );
}

function RequestDetailModal({ request, suggestion, loadingSuggestion, suggestionError, onClose, onCreateEvent, onDeleteRequest, isDeleting, formatDate }) {
  const statusConfig = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
  const category = CATEGORY_LABELS[request.category];
  const hasSuggestion = !!request.suggestionID;
  const suggestionLoaded = suggestion && suggestion.eventName;
  const confidencePct =
    suggestionLoaded && suggestion.confidenceScore != null
      ? Math.round(suggestion.confidenceScore * 100)
      : null;

  return (
    <Dialog open={true} onClose={onClose} className="relative z-[60]">
      <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-[60] flex items-end sm:items-start justify-center sm:p-4 lg:p-8">
        <DialogPanel className="relative w-full sm:max-w-2xl rounded-t-2xl sm:rounded-lg bg-white dark:bg-gray-800 shadow-xl flex flex-col max-h-[90vh] sm:max-h-[calc(100vh-2rem)] lg:max-h-[calc(100vh-4rem)]">
          {/* Mobile drag handle */}
          <div className="sm:hidden flex justify-center pt-2.5 pb-1 shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>

          <button
            onClick={onClose}
            className="absolute top-2 sm:top-3 right-2 sm:right-3 rounded-full p-1.5 text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors z-10"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Action bar */}
          <div className="flex-shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sm:rounded-t-lg">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onCreateEvent}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primaryDark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <PlusIcon className="h-4 w-4" />
                Create Event
              </button>
              {onDeleteRequest && (
                <button
                  type="button"
                  onClick={onDeleteRequest}
                  disabled={isDeleting}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-300 dark:border-red-800 bg-white dark:bg-gray-800 px-3 py-2 text-sm font-semibold text-red-600 dark:text-red-300 shadow-sm hover:bg-red-50 dark:hover:bg-red-900/40 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-500"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span className="hidden sm:inline">Delete Request</span>
                  <span className="sm:hidden">Delete</span>
                </button>
              )}
            </div>
            <span className="hidden md:inline text-xs text-gray-400 dark:text-gray-500 pr-8">Opens the AI creator in a new tab</span>
          </div>

          <div className="flex-1 overflow-y-auto">
          {/* Request section */}
          <div className="p-6 space-y-4">
            <div>
              <p className="text-[11px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1">
                Event Request
              </p>
              <div className="flex items-start justify-between gap-3">
                <DialogTitle as="h2" className="text-xl font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                  {request.eventName || "Event Request"}
                </DialogTitle>
                <span
                  className={`shrink-0 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusConfig.bg} ${statusConfig.text}`}
                >
                  {statusConfig.label}
                </span>
              </div>
              {category && (
                <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                  {category.emoji} {category.label}
                </span>
              )}
            </div>

            {/* Submitter */}
            <div className="flex items-center gap-3 rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2.5">
              <div className="h-10 w-10 flex-shrink-0">
                {request.submittedBy?.photoURL ? (
                  <img
                    className="h-10 w-10 rounded-full object-cover"
                    src={variantURL(request.submittedBy.photoURL, 200)}
                    onError={fallbackToOriginal(request.submittedBy.photoURL)}
                    alt=""
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-base font-semibold">
                    {(request.submittedBy?.userName || "?").charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {request.submittedBy?.userName || "Unknown"}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {request.submittedBy?.userEmail || request.submittedBy?.userPhone || ""}
                </div>
              </div>
            </div>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {request.date && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">When</dt>
                  <dd className="text-gray-800 dark:text-gray-200">{formatDate(request.date)}</dd>
                </div>
              )}
              {request.location && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Where</dt>
                  <dd className="text-gray-800 dark:text-gray-200">{request.location}</dd>
                </div>
              )}
              {request.createdAt && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Submitted</dt>
                  <dd className="text-gray-800 dark:text-gray-200">{formatDate(request.createdAt)}</dd>
                </div>
              )}
              {request.updatedAt && (
                <div>
                  <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Last updated</dt>
                  <dd className="text-gray-800 dark:text-gray-200">{formatDate(request.updatedAt)}</dd>
                </div>
              )}
            </dl>

            {request.location && <LocationMap location={request.location} />}

            {request.description && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Description</p>
                <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                  {request.description}
                </p>
              </div>
            )}
          </div>

          {/* Linked idea section */}
          {hasSuggestion && (
            <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              {loadingSuggestion && (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
                </div>
              )}

              {suggestionError && (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-gray-600 dark:text-gray-300">{suggestionError}</p>
                </div>
              )}

              {suggestionLoaded && (
                <>
                  {suggestion.imageUrl && (
                    <div className="w-full aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
                      <img
                        src={variantURL(suggestion.imageUrl, 800)}
                        onError={fallbackToOriginal(suggestion.imageUrl)}
                        alt={suggestion.eventName}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}

                  <div className="p-6 space-y-4">
                    <div>
                      <p className="text-[11px] font-medium text-primary uppercase tracking-wide mb-1 flex items-center gap-1.5">
                        <SparklesIcon className="h-3.5 w-3.5" />
                        Original AI Idea
                      </p>
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 leading-snug">
                          {suggestion.eventName}
                        </h3>
                        {confidencePct != null && (
                          <span className="shrink-0 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                            {confidencePct}% confidence
                          </span>
                        )}
                      </div>
                      {suggestion.theme && (
                        <span className="mt-2 inline-block bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-full px-2.5 py-0.5 text-xs font-medium">
                          {suggestion.theme}
                        </span>
                      )}
                    </div>

                    <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      {suggestion.suggestedDate && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">When</dt>
                          <dd className="text-gray-800 dark:text-gray-200">
                            {formatDate(suggestion.suggestedDate)}
                            {suggestion.suggestedTime && ` · ${suggestion.suggestedTime}`}
                          </dd>
                        </div>
                      )}
                      {(suggestion.venue || suggestion.venueAddress) && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Where</dt>
                          <dd className="text-gray-800 dark:text-gray-200">
                            {suggestion.venue}
                            {suggestion.venueAddress && (
                              <span className="block text-xs text-gray-500 dark:text-gray-400">{suggestion.venueAddress}</span>
                            )}
                          </dd>
                        </div>
                      )}
                      {suggestion.targetAudience && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Audience</dt>
                          <dd className="text-gray-800 dark:text-gray-200">{suggestion.targetAudience}</dd>
                        </div>
                      )}
                      {suggestion.ageRange && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Age range</dt>
                          <dd className="text-gray-800 dark:text-gray-200">{suggestion.ageRange}</dd>
                        </div>
                      )}
                      {suggestion.groupSize && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Group size</dt>
                          <dd className="text-gray-800 dark:text-gray-200">{suggestion.groupSize}</dd>
                        </div>
                      )}
                      {suggestion.estimatedCost && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Cost</dt>
                          <dd className="text-gray-800 dark:text-gray-200">{suggestion.estimatedCost}</dd>
                        </div>
                      )}
                      {suggestion.projectedRevenue != null && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Projected revenue</dt>
                          <dd className="text-gray-800 dark:text-gray-200">${suggestion.projectedRevenue}</dd>
                        </div>
                      )}
                      {suggestion.estimatedAttendance != null && (
                        <div>
                          <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Est. attendance</dt>
                          <dd className="text-gray-800 dark:text-gray-200">{suggestion.estimatedAttendance}</dd>
                        </div>
                      )}
                    </dl>

                    {suggestion.description && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Idea description</p>
                        <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed whitespace-pre-line">
                          {suggestion.description}
                        </p>
                      </div>
                    )}

                    {suggestion.reasoning && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Why this event?</p>
                        <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed italic">
                          {suggestion.reasoning}
                        </p>
                      </div>
                    )}

                    {(suggestion.trendingEventReference ||
                      (suggestion.sourceUrls && suggestion.sourceUrls.length > 0)) && (
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 text-xs text-gray-500 dark:text-gray-400">
                        {suggestion.trendingEventReference && (
                          <p className="mb-2">
                            <span className="font-medium">Inspired by:</span>{" "}
                            <span className="italic">{suggestion.trendingEventReference}</span>
                          </p>
                        )}
                        {suggestion.sourceUrls && suggestion.sourceUrls.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {suggestion.sourceUrls.map((url, i) => (
                              <a
                                key={i}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-0.5 text-primary hover:underline"
                              >
                                Source {i + 1}
                                <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function LocationMap({ location }) {
  const query = encodeURIComponent(location);
  const embedUrl = `https://maps.google.com/maps?q=${query}&z=13&output=embed`;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${query}`;
  return (
    <div>
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Where on the map</p>
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="relative h-48 sm:h-56 w-full bg-gray-100 dark:bg-gray-800">
          <iframe
            title="Event location preview"
            src={embedUrl}
            width="100%"
            height="100%"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
        <a
          href={mapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between gap-2 px-4 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <span className="truncate text-gray-700 dark:text-gray-200">{location}</span>
          <span className="flex-shrink-0 inline-flex items-center gap-0.5 text-primary hover:underline">
            Open in Maps
            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
          </span>
        </a>
      </div>
    </div>
  );
}
